import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams } from 'react-router';
import { ChatInterface } from '@/components/chat/ChatInterface';
import { ConversationListPanel } from '@/components/conversations/ConversationListPanel';
import { ConversationDrawer } from '@/components/conversations/ConversationDrawer';
import { ApprovalGateSheet } from '@/components/chat/ApprovalGateSheet';
import { useIsMobile } from '@/hooks/useIsMobile';
import { OPEN_CONVERSATION_DRAWER_EVENT } from '@/components/layout/Layout';

const PANEL_MIN = 220;
const PANEL_MAX = 420;
const PANEL_DEFAULT = 260;
const STORAGE_KEY = 'archon-chat-panel-width';

function getInitialWidth(): number {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const parsed = Number(stored);
    if (parsed >= PANEL_MIN && parsed <= PANEL_MAX) return parsed;
  }
  return PANEL_DEFAULT;
}

export function ChatPage(): React.ReactElement {
  const { '*': rawConversationId } = useParams();
  const conversationId = rawConversationId ? decodeURIComponent(rawConversationId) : undefined;

  const [width, setWidth] = useState(getInitialWidth);
  const isResizing = useRef(false);

  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const open = (): void => {
      setDrawerOpen(true);
    };
    window.addEventListener(OPEN_CONVERSATION_DRAWER_EVENT, open);
    return (): void => {
      window.removeEventListener(OPEN_CONVERSATION_DRAWER_EVENT, open);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(width));
  }, [width]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent): void => {
      e.preventDefault();
      isResizing.current = true;
      const startX = e.clientX;
      const startWidth = width;

      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      const onMouseMove = (moveEvent: MouseEvent): void => {
        const newWidth = Math.min(
          PANEL_MAX,
          Math.max(PANEL_MIN, startWidth + moveEvent.clientX - startX)
        );
        setWidth(newWidth);
      };

      const onMouseUp = (): void => {
        isResizing.current = false;
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [width]
  );

  if (isMobile) {
    return (
      <div className="flex flex-1 flex-col overflow-hidden">
        {conversationId ? (
          <>
            <ChatInterface key={conversationId} conversationId={conversationId} />
            <ApprovalGateSheet conversationId={conversationId} />
          </>
        ) : (
          <ConversationListPanel />
        )}
        <ConversationDrawer
          open={drawerOpen}
          onClose={(): void => {
            setDrawerOpen(false);
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Left panel (desktop sidebar) */}
      <div
        className="relative flex h-full flex-col border-r border-border bg-surface overflow-hidden"
        style={{ width: `${String(width)}px`, flexShrink: 0 }}
      >
        <ConversationListPanel />
        {/* Resize handle */}
        <div
          onMouseDown={handleMouseDown}
          className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize bg-border/50 hover:bg-primary/40 transition-colors"
        />
      </div>

      {/* Right panel - chat interface */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <ChatInterface key={conversationId ?? 'new'} conversationId={conversationId ?? 'new'} />
      </div>
    </div>
  );
}
